import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../../../../../hooks";
import proofkitFilemakerService from '../../../../../services/proofkitFilemakerService';
import { ArrowLeft, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label } from "../../../../../components/ui";

const CONTACTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACTS || "contacts";

export default function ContactsFormEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recordId, setRecordId] = useState(id);

  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
    fuente: "",
    referente_nombre: "",
    referente_apellido: "",
  });

  useEffect(() => {
    fetchContact();
  }, [id]);

  const fetchContact = async () => {
    try {
      setLoading(true);

      try {
        const res = await proofkitFilemakerService.getRecord(CONTACTS_LAYOUT, id);
        const rec = res?.data || null;
        const fd = rec?.fieldData || {};

        setRecordId(rec?.recordId || id);
        setFormData({
          nombre: fd?.nombre || "",
          apellido: fd?.apellido || "",
          email: fd?.email || "",
          fuente: fd?.fuente || "",
          referente_nombre: fd?.referente_nombre || "",
          referente_apellido: fd?.referente_apellido || "",
        });

        return;
      } catch (_) {
        const findRes = await proofkitFilemakerService.findRecords(CONTACTS_LAYOUT, {
          query: [{ ClavePrincipal: id }],
          limit: 1,
        });

        const rec = findRes?.data?.[0] || null;
        const fd = rec?.fieldData || {};

        if (!rec?.recordId) {
          throw new Error("Registro no encontrado");
        }

        setRecordId(rec.recordId);
        setFormData({
          nombre: fd?.nombre || "",
          apellido: fd?.apellido || "",
          email: fd?.email || "",
          fuente: fd?.fuente || "",
          referente_nombre: fd?.referente_nombre || "",
          referente_apellido: fd?.referente_apellido || "",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo cargar el contacto",
        variant: "destructive",
      });
      navigate("/campanas/contacts");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const nombre = String(formData?.nombre || "").trim();
    const apellido = String(formData?.apellido || "").trim();
    const email = String(formData?.email || "").trim();

    if (!nombre) {
      return toast({
        title: "Falta nombre",
        description: "Debes indicar el nombre del contacto",
        variant: "destructive",
      });
    }

    if (!email) {
      return toast({
        title: "Falta email",
        description: "Debes indicar el email del contacto",
        variant: "destructive",
      });
    }

    if (!validateEmail(email)) {
      return toast({
        title: "Email inválido",
        description: "El formato del email no es válido",
        variant: "destructive",
      });
    }

    try {
      setSaving(true);

      await proofkitFilemakerService.updateRecord(CONTACTS_LAYOUT, recordId, {
        nombre,
        apellido,
        email,
        fuente: String(formData?.fuente || "").trim(),
        referente_nombre: String(formData?.referente_nombre || "").trim(),
        referente_apellido: String(formData?.referente_apellido || "").trim(),
      });

      toast({
        title: "Éxito",
        description: "Contacto actualizado correctamente",
      });
      navigate(`/campanas/contacts/${id}`);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo actualizar el contacto",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Cargando contacto...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(`/campanas/contacts/${id}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <Button onClick={onSubmit} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Editar Contacto</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={onSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input id="nombre" name="nombre" value={formData.nombre} onChange={handleInputChange} required />
              </div>

              <div className="space-y-2">
                <Label htmlFor="apellido">Apellido</Label>
                <Input id="apellido" name="apellido" value={formData.apellido} onChange={handleInputChange} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fuente">Fuente</Label>
                <Input id="fuente" name="fuente" value={formData.fuente} onChange={handleInputChange} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="referente_nombre">Referente nombre</Label>
                <Input id="referente_nombre" name="referente_nombre" value={formData.referente_nombre} onChange={handleInputChange} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="referente_apellido">Referente apellido</Label>
                <Input id="referente_apellido" name="referente_apellido" value={formData.referente_apellido} onChange={handleInputChange} />
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}